import type React from 'react';
import { useWizard, WizardStep } from '@zillow/zrs-rental-applications';
import {
  useHandleScreeningReports,
  useId,
  useIdVerificationCallbacks,
  usePassiveAuthData,
  usePidAnswers,
  useReauthenticating,
  useSetKiqQuestionSet,
  useSetLoading,
  useTermSet,
} from '../../contexts/IdVerificationContext';
import { Box, Button, Heading, Icon, Paragraph, Text } from '@zillow/constellation-10';
import { IconWarningFilled } from '@zillow/constellation-icons';
import { ExitReason, IdvStep, LogLevel, TermSet } from '../../../../models';
import { LANDLORD_CLICKSTREAM_EVENTS, RENTER_CLICKSTREAM_EVENTS } from '../../clickstream/events';
import { useIdVerificationWebApiHandler_performIdentityCheck } from '@zg-rentals/ts-id-verification-client-react-query';
import { formatPhoneNumber } from '../../format/format';
import type { ButtonProps } from '../../NextButton/NextButton';
import { getZgAuthZoneFromTermSet } from '../../utils/zgAuthZoneUtils';
import {
  FROZEN_ACCOUNT_SUBSTEP,
  GENERIC_ERROR_SUBSTEP,
  USER_NOT_FOUND_LOCKED_OUT_SUBSTEP,
  USER_NOT_FOUND_SUBSTEP,
} from '../../constants/steps';
import { ErrorCodes } from '../../constants/errorCodes';
import { DatarouterError } from '@zg-rentals/web-base';
import { useEffectOnce } from 'react-use';
import { LANDLORD_METRIC_PREFIX, RENTER_METRIC_PREFIX } from '../../IdVerification';

interface ReviewStepProps {
  id: string;
  label: string;
}

const EMPTY_FIELD_TEXT = 'Not provided';

function Dt({ children }: { children: React.ReactNode }) {
  return (
    <Text asChild textStyle="body-sm-bold">
      <dt>{children}</dt>
    </Text>
  );
}

function Dd({ children, ...props }: { children: React.ReactNode; css?: React.ComponentProps<typeof Text>['css'] }) {
  return (
    <Text asChild {...props}>
      <dd>{children}</dd>
    </Text>
  );
}

/* eslint-disable curly-quotes/no-straight-quotes */
const renterTemplateArea = `
  ". head"
  ". subHead"
  ". firstName"
  "middleNameWarn middleName"
  ". lastName"
  ". dob"
  ". phone"
  "ssnWarn ssn"
  ". address"
`;

const landlordTemplateArea = `
  ". head"
  ". subHead"
  ". firstName"
  "middleNameWarn middleName"
  ". lastName"
  "ssnWarn ssn"
  ". address"
`;
/* eslint-enable curly-quotes/no-straight-quotes */

export default function ReviewStep({ id, label }: ReviewStepProps) {
  const pidAnswers = usePidAnswers();
  const { firstName, middleName, lastName, currentAddress, currentCity, currentState, currentZip, dob, phone, ssn } =
    pidAnswers;
  const { jsc, hdimPayload } = usePassiveAuthData();
  const termSet = useTermSet();
  const requestingClient = useId();
  const handleScreeningReports = useHandleScreeningReports();
  const { onKbaStepUp, onNotAuthenticated, onSubmitPidForm, trackEvent, emitMetric, log, onExit } =
    useIdVerificationCallbacks();
  const reauthenticating = useReauthenticating();
  const setLoading = useSetLoading();
  const setKiqQuestionSet = useSetKiqQuestionSet();
  const { onRequestStep, currentStep } = useWizard();

  useEffectOnce(() => {
    trackEvent(LANDLORD_CLICKSTREAM_EVENTS.viewPage('id_verification_review_personal_information'));
  });

  const { mutateAsync } = useIdVerificationWebApiHandler_performIdentityCheck({
    onMutate() {
      setLoading(true);
      const hasPassiveAuthData = hdimPayload && jsc;
      if (!hasPassiveAuthData) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} missingJscOrHdim`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} missingJscOrHdim`);
        }
      }

      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNextSubmitIDV);
      } else if (ssn) {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitIdvWithSsn);
      } else {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitIdv);
      }
      onSubmitPidForm({
        ...pidAnswers,
        // Don't include sensitive information
        ssn: '',
        dob: '',
      });
    },
    onSuccess: async (data) => {
      if (data.passiveAuthSucceeded) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} passiveAuth success`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} passiveAuth success`);
        }

        if (termSet === TermSet.LandlordScreening) {
          trackEvent(LANDLORD_CLICKSTREAM_EVENTS.idvSuccess);
        }
        await onExit({ reason: ExitReason.IdVerified, success: true });
      } else if (data.kiqEnabled && data.questionSet && data.questionSet.length > 0) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} kbaStepUp`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} kbaStepUp`);
        }
        onKbaStepUp();
        const nextStep = currentStep === IdvStep.KiqOnboardingStep ? IdvStep.KiqStep : IdvStep.KiqOnboardingStep;
        setKiqQuestionSet(data.questionSet);
        onRequestStep(nextStep);
      } else {
        log(LogLevel.Error, {
          message: 'Error calling performPassiveIdentityCheck - no question set',
        });
        onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
      }
    },
    onError(error) {
      if (termSet === TermSet.LandlordScreening) {
        emitMetric(`${LANDLORD_METRIC_PREFIX} performIdentityCheck error`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} performIdentityCheck error`);
      }
      if (DatarouterError.is(error)) {
        if (error.responseStatus === 401) {
          onNotAuthenticated();
          onExit({ reason: ExitReason.NotAuthenticated, success: false });
        } else {
          switch (error.code) {
            case ErrorCodes.USER_NOT_FOUND:
              onRequestStep(IdvStep.ErrorStep, USER_NOT_FOUND_SUBSTEP);
              break;
            case ErrorCodes.USER_NOT_FOUND_LOCKED_OUT:
              onRequestStep(IdvStep.ErrorStep, USER_NOT_FOUND_LOCKED_OUT_SUBSTEP);
              break;
            case ErrorCodes.FROZEN_CREDIT_REPORT:
              onRequestStep(IdvStep.ErrorStep, FROZEN_ACCOUNT_SUBSTEP);
              break;
            default:
              log(LogLevel.Error, {
                message: `Unexpected error code for performIdentityCheck: ${error.code}`,
                error,
              });
              onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
              break;
          }
        }
      } else {
        log(LogLevel.Error, {
          message: 'Unexpected error response for performIdentityCheck',
          error,
        });
        onRequestStep(IdvStep.ErrorStep, GENERIC_ERROR_SUBSTEP);
      }
    },
    onSettled() {
      setLoading(false);
    },
  });

  return (
    <WizardStep
      id={id}
      label={label}
      meta={{
        renderNextButton: (buttonProps: ButtonProps) => (
          <Button {...buttonProps} tone="brand" emphasis="filled">
            Verify identity
          </Button>
        ),
      }}
      onSubmit={async () => {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNext('id_verification_review_personal_information'));
        if (!ssn && reauthenticating) {
          await onExit({ reason: ExitReason.AttemptedReAuthWithoutSsn, success: false });
          return;
        }
        await mutateAsync({
          postData: {
            ...pidAnswers,
            deviceType: 'desktop',
            requestingClient,
            zgAuthZone: getZgAuthZoneFromTermSet(termSet),
            isReAuthRequest: reauthenticating,
            hdimPayload,
            jsc,
          },
        });
      }}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onSuccess={(_: any, abortController: AbortController) => {
        abortController.abort();
      }}
      render={() => (
        <Box
          asChild
          css={{
            display: 'grid',
            rowGap: 'default',
            gridTemplateColumns: 'max-content 1fr',
            gridTemplateAreas: termSet === TermSet.RenterScreening ? renterTemplateArea : landlordTemplateArea,
          }}
        >
          <dl>
            <Box css={{ gridArea: 'head' }}>
              <Heading level={2} textStyle="heading-sm" css={{ marginBottom: 'default' }}>
                Review your personal information
              </Heading>
            </Box>
            <Box css={{ gridArea: 'subHead' }}>
              <Paragraph css={{ marginBottom: 'default' }}>
                {termSet === TermSet.RenterScreening
                  ? 'The information you previously entered will be sent to Experian to verify your identity. If it’s incorrect, you may not be able to submit the application.'
                  : 'The information you previously entered will be used to verify your identity. If it’s incorrect, you may not be able to review your applicants’ information.'}
              </Paragraph>
            </Box>
            <Box css={{ gridArea: 'firstName', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>Legal first name</Dt>
              <Dd>{firstName}</Dd>
            </Box>
            <Box css={{ gridArea: 'middleNameWarn', marginRight: 'tighter' }}>
              {!middleName ? (
                <Icon css={{ marginTop: 'tightest', color: 'icon.feedback.warning.hero' }}>
                  <IconWarningFilled />
                </Icon>
              ) : null}
            </Box>
            <Box css={{ gridArea: 'middleName', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>Legal middle name</Dt>
              <Dd css={{ color: middleName ? 'text.neutral' : 'text.subtle' }}>
                {middleName ? middleName : EMPTY_FIELD_TEXT}
              </Dd>
              {!middleName && (
                <Paragraph textStyle="fineprint" css={{ color: 'text.feedback.warning.hero' }}>
                  If you have a middle name, you need to add it so that your identity can be verified
                </Paragraph>
              )}
            </Box>
            <Box css={{ gridArea: 'lastName', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>Legal last name</Dt>
              <Dd>{lastName}</Dd>
            </Box>
            {termSet === TermSet.RenterScreening && (
              <>
                <Box css={{ gridArea: 'dob', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
                  <Dt>Date of birth</Dt>
                  <Dd>{dob}</Dd>
                </Box>
                <Box css={{ gridArea: 'phone', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
                  <Dt>Phone number</Dt>
                  <Dd>{formatPhoneNumber(phone)}</Dd>
                </Box>
              </>
            )}
            <Box css={{ gridArea: 'ssnWarn', marginRight: 'tighter' }}>
              {!ssn && handleScreeningReports ? (
                <Icon css={{ marginTop: 'tightest', color: 'icon.feedback.warning.hero' }}>
                  <IconWarningFilled />
                </Icon>
              ) : null}
            </Box>
            <Box css={{ gridArea: 'ssn', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>{termSet === TermSet.RenterScreening ? 'SSN or ITIN' : 'Social Security number'}</Dt>
              {ssn ? (
                <Dd>XXX-XX-XXXX</Dd>
              ) : (
                <>
                  <Paragraph css={{ color: 'text.subtle' }}>{EMPTY_FIELD_TEXT}</Paragraph>
                  {handleScreeningReports && (
                    <Paragraph textStyle="fineprint" css={{ color: 'text.feedback.warning.hero' }}>
                      {termSet === TermSet.RenterScreening
                        ? 'Add your SSN or ITIN to accurately verify your identity and pull the correct screening reports'
                        : 'If you have a Social Security number, you need to add it so that your identity can be verified'}
                    </Paragraph>
                  )}
                </>
              )}
            </Box>
            <Box css={{ gridArea: 'address', display: 'flex', flexDirection: 'column', gap: 'tighter' }}>
              <Dt>Current address</Dt>
              {/* We use a <br/> instead of 2 paragraphs because 'dd' and 'dt' tags should alternate with each other */}
              <Dd>
                {currentAddress}
                <br />
                {`${currentCity}, ${currentState} ${currentZip}`}
              </Dd>
            </Box>
          </dl>
        </Box>
      )}
    />
  );
}
